'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";
import Loader from "@uw-it-sis/lib-react/lib/Loader";

import { FaKey, FaCheck, FaExclamationTriangle, FaRegFileAlt } from "react-icons/fa";

import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Table from "react-bootstrap/Table";

import events from "@uw-it-sis/lib-react/lib/Events";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";

import pinnedSchedulesStore from "./PinnedSchedulesStore";
import ScheduleBuilderOptionCourse from "./ScheduleBuilderOptionCourse";
import ScheduleBuilderOptionModalStore from "./ScheduleBuilderOptionModalStore";
import {ScheduleBuilderEvents} from "./ScheduleBuilderConstants";
import RegistrationDateInfo from "./RegistrationDateInfo";
import {timeMaskCountForSchedule, hasDupEnrollForSchedule, buildSlnData, getSectionCodesFromScheduleOption} from "./ScheduleBuilderUtils";
import RegisterButton from "../common/RegisterButton";
import RegisterButtonStore from "../common/RegisterButtonStore";
import registrationDetailsStore from '../registration/RegistrationDetailsStore';
import {getSectionLegendFlags} from "../common/Utils";


class ScheduleBuilderOptionModal extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = new ScheduleBuilderOptionModalStore();

        this.onAlternateSelect = this.onAlternateSelect.bind(this);
        // this.onToggleSendToRegistration = this.onToggleSendToRegistration.bind(this);
        this.toggleAlternate = this.toggleAlternate.bind(this);
        this.analyticsStore = appStore.getAnalyticsStore();
    }

    componentDidMount() {
        this.store.mount(this.props.schedule);

        RegisterButtonStore.initializeData(this.props.schedule.termId);

        events.listen(ScheduleBuilderEvents.pinnedSchedulesLoaded, () => {
            // If modal is being shown and change in pinned schedules happens, then force modal to update.
            if (this.props.show) {
                this.forceUpdate();
            }
        });
    }

    componentWillUnmount() {
        events.remove(ScheduleBuilderEvents.pinnedSchedulesLoaded, () => {
            // If modal is being shown and change in pinned schedules happens, then force modal to update.
            if (this.props.show) {
                this.forceUpdate();
            }
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.show !== prevProps.show) {
            this.store.mount(this.props.schedule);
        }
    }

    // Action to pin the schedule for later
    pinAction(e, pinnedScheduleId) {
        e.stopPropagation();
        let context = "SB-Modal";
        if (pinnedScheduleId) {
            // If pinned id arg is passed, delete pin
            pinnedSchedulesStore.deletePinnedSchedule(pinnedScheduleId, context);
        } else {
            // If no pinned id, then schedule is to be added to pin
            pinnedSchedulesStore.addPinnedSchedule(this.props.schedule, context);
        }
    }

    addToPlanAction(e, isPinned) {
        e.stopPropagation();

        let submittedAlternates = this.store.selectedAlternateCodes;
        let context = "SB-selection-option";

        if (isPinned) {
            context = this.props.isFromPinnedMenu ? "SB-menu-pinned" : "SB-selection-pinned";
        }

        this.store.selectForPlan(this.props.schedule, submittedAlternates, this.props.regDate, context, this.props.schedule.termId);
    }

    sendToRegisterAction(e, isPinned) {
        e.stopPropagation();

        const localTime = new Date();
        const dataStateSummary = registrationDetailsStore.getCurrentDataStateSummary();
        const submittedAlternates = this.store.selectedAlternateCodes;
        const selectedCodes = getSectionCodesFromScheduleOption(this.props.schedule, submittedAlternates);
        const additionalData = {
            regdCnt: dataStateSummary.registeredCount,
            cIssuesCnt: dataStateSummary.courseIssuesCount,
            sIssuesCnt: dataStateSummary.sectionIssuesCount,
            readyCnt: dataStateSummary.readyCount,
            holdsCnt: dataStateSummary.holdsCount,
            regDate: dataStateSummary.registrationDate,
            selCnt: selectedCodes.length,
            localTimestamp: localTime.toISOString()
        }
        this.analyticsStore.addEvent("SB-RegButtonClick", {}, additionalData);


        let context = `SB-${isPinned ? 'pinned':'option'}-register`;
        this.store.selectForPlan(this.props.schedule, submittedAlternates, this.props.regDate, context, this.props.schedule.termId);
    }

    onAlternateSelect(e) {
        e.stopPropagation();
        let tokens = e.target.value.split("|");
        let parentActivityId = tokens[0];
        let sectionCode = tokens[1];
        let isPrimary = false;
        if (tokens.length > 2) {
            isPrimary = tokens[2] === "primary";
        }


        if (isPrimary) {
            this.store.chooseAlternatePrimarySection(parentActivityId, sectionCode);
        }
        else {
            this.store.chooseAlternateSecondarySection(parentActivityId, sectionCode);
        }
    }

    toggleAlternate(e) {
        e.stopPropagation();

        this.store.toggleDisplayAlternates(e.target.value);
    }

    cleanupAndHide() {
        this.store.reset();
        this.store.resetData();
        this.props.onHide();
    }

    // onToggleSendToRegistration() {
    //     this.store.toggleShowSendToRegistration(this.props.schedule, this.store.selectedAlternateCodes);
    // }

    render() {
        const selectedAlternateCodes = [].concat(this.store.selectedAlternateCodes);
        const alternatePrimaryActivityIds = [].concat(this.store.getPrimaryActivityIdsRequiringAlternateSelection());
        const termUrl = `/plan/#/${TermUtils.makeTermLinkIdentifierFromTermId(this.props.schedule.termId)}`;
        const pinnedScheduleData = pinnedSchedulesStore.getSchedulePinnedData(this.props.schedule.activityHash);
        const isPinned = !isEmpty(pinnedScheduleData);

        let modalTitle = this.props.title;

        let possibleErrorsFeedback;
        if (
            this.props.schedule.possibleErrors
            &&
            this.props.schedule.possibleErrors.hasOwnProperty("errorMessagesByCourseCd")
            &&
            !isEmpty(this.props.schedule.possibleErrors.errorMessagesByCourseCd)
            &&
            !this.store.showSendToRegistration
        ) {
            possibleErrorsFeedback =
                <Alert variant="warning" className="alert-color-match d-flex align-items-start mt-3 mx-3">
                    <FaExclamationTriangle className="me-3 mt-1" aria-hidden={true} />
                    <div>The following courses have one or more issues. Revise your plan and update the schedule options.</div>
                </Alert>;
        }

        let courseList = this.store.preparedScheduleItems.map((preparedSchedule, index) => {
            const item = preparedSchedule.item;

            const hasAlternates = this.store.courseCodesWithAlternates.includes(item.courseCd);
            const showAlternates = this.store.data.displayAlternatesPrimaryActivityIds.includes(item.activityOfferingId);

            let errorMessages;
            if (
                this.props.schedule.possibleErrors
                &&
                this.props.schedule.possibleErrors.hasOwnProperty("errorMessagesByCourseCd")
                &&
                this.props.schedule.possibleErrors.errorMessagesByCourseCd.hasOwnProperty(item.courseCd)
            ) {
                errorMessages = this.props.schedule.possibleErrors.errorMessagesByCourseCd[item.courseCd];
            }

            return (
                <ScheduleBuilderOptionCourse
                    item={item}
                    preparedActivities={preparedSchedule.courseActivities}
                    alternatePrimaryList={preparedSchedule.alternatePrimaryList}
                    onToggleAlternates={this.toggleAlternate}
                    showAlternates={showAlternates}
                    key={index}
                    hasAlternates={hasAlternates}
                    onSelectAlternate={this.onAlternateSelect}
                    showSendToRegistration={this.store.showSendToRegistration}
                    dataSubmitted={this.store.data.submitted}
                    selectedAlternateCodes={selectedAlternateCodes}
                    alternatePrimaryActivityIds={alternatePrimaryActivityIds}
                    showCourseHeading={!(this.store.showSendToRegistration && this.store.data.submitted)}
                    showBorder={true}
                    planData={this.props.planData}
                    errorMessages={errorMessages}
                />
            );
        });

        /* Pin this Schedule action button */
        let pinAction;
        if ( !this.store.data.submitted ) {
            let pinTitle = `${isPinned ? 'Unpin':'Pin'} this Option`;
            let pinLabel = <>{isPinned ? 'Unpin':'Pin'} <span className="d-none d-md-inline">this </span>Option</>;
            pinAction =
                <Button variant="secondary"
                        className="px-4"
                        title={pinTitle}
                        disabled={appStore.isAdviser() || this.store.state === States.pending}
                        onClick={(e) => this.pinAction(e, (isPinned ? pinnedScheduleData.id : null))}>
                    {pinLabel}
                </Button>;
            if (pinnedSchedulesStore.state === States.pending) {
                pinAction =
                    <Button variant="secondary"
                            className="px-4"
                            className="d-flex align-items-center"
                            title={pinTitle}
                            disabled>
                        <Spinner animation="border" size="sm" className="me-2 align-middle" aria-hidden="true"/>
                        {pinLabel}
                    </Button>;
            }
        }

        /* Send All to Registration action button */
        // let sendToRegistrationAction;
        // if (RegisterButtonStore.showButton() && !this.store.showSendToRegistration && (appStore.isStudent() || appStore.isAdviser())) {
        //     sendToRegistrationAction =
        //         <Button
        //             variant="primary"
        //             className="px-4"
        //             onClick={this.onToggleSendToRegistration}>
        //             Review & Send to Registration
        //         </Button>;
        // }

        /* Add All to Plan action button */
        let addToPlanAction =
            <Button
                variant={'primary'}
                className="px-4"
                onClick={(e) => this.addToPlanAction(e, isPinned)}
                disabled={appStore.isAdviser() || this.store.state === States.pending}>
                Update <span className="d-none d-md-inline">My </span>Plan
            </Button>;

        /* Legends and messaging */
        let enrollRestrictionLegend;
        let addCodeLegend;

        let sectionLegendFlags = getSectionLegendFlags(this.props.schedule.activityOptions, "enrollmentRestriction");

        if (sectionLegendFlags.enrollRestriction) {
            enrollRestrictionLegend = <div><FaRegFileAlt className="me-1" aria-hidden={true} /> Section has enrollment restrictions</div>;
        }

        if (sectionLegendFlags.addCode) {
            addCodeLegend = <div><FaKey className="me-1" aria-hidden={true} /> Section requires entry code</div>;
        }

        let timeMaskLegend;
        let timeMaskSectionCount = timeMaskCountForSchedule(this.props.schedule);
        if ( timeMaskSectionCount > 0 ) {
            let quantityToken = "These sections";
            if (timeMaskSectionCount === 1) {
                quantityToken = "This section";
            }
            timeMaskLegend = <small className="d-block px-2 mt-2 mb-0">* {quantityToken} will ignore any schedule conflicts or date/time filters.</small>;
        }
        let dupEnrollLegend;
        if ( hasDupEnrollForSchedule(this.props.schedule) ) {
            dupEnrollLegend = <small className="d-block px-2 mt-2 mb-0"><sup>&dagger;</sup> These are primary sections that can be registered concurrently.</small>;
        }

        let pinLegend;
        if ( !isPinned ) {
            pinLegend = <p className="mt-3 mb-0"><strong>Pin</strong> this schedule option to save it in Schedule Builder without affecting your planned courses.</p>
        }

        let registrationIsOpenMessage;
        if (RegisterButtonStore.showButton() && (appStore.isStudent() || appStore.isAdviser())) {
            registrationIsOpenMessage = <p className="mt-3 mb-0 text-purple fw-bold">Registration is open. You will be able to register online until {RegisterButtonStore.getLastAddDate()}</p>
        }

        const loaderSpinner = <Spinner
            as="span"
            size="sm"
            animation="border"
            role="status"
            aria-hidden="true"
        />;

        let modalFooter;
        if ( (pinAction || addToPlanAction) && !this.store.showSendToRegistration) {
            modalFooter =
                <div className="mt-3 mx-0">
                    <Loader show={this.store.state === States.pending} message={loaderSpinner} disableDefaultStyles>
                        <ButtonToolbar className="d-flex justify-content-end">
                            {pinAction}
                            {addToPlanAction}
                            {/*{sendToRegistrationAction}*/}
                        </ButtonToolbar>
                    </Loader>
                </div>;
        }

        let footerHeading;

        /* Registration Mode */
        // if (this.store.showSendToRegistration) {
        //     modalTitle = "Review your schedule before sending to Registration";
        //     // Get the list of sections we want to pass to SPS
        //     let selectedCodes = getSectionCodesFromScheduleOption(this.props.schedule, this.store.selectedAlternateCodes);
        //     modalFooter =
        //         <div className="mt-3 mx-0">
        //             <Loader show={this.store.state === States.pending} message={loaderSpinner} disableDefaultStyles>
        //                 <ButtonToolbar className="d-flex justify-content-between">
        //                     <Button
        //                         variant="secondary"
        //                         className="px-4"
        //                         onClick={this.onToggleSendToRegistration}>Back</Button>
        //                     <RegisterButton
        //                         slnData={buildSlnData(this.props.schedule, selectedCodes)}
        //                         termId={this.props.schedule.termId}
        //                         onClick={(e) => this.sendToRegisterAction(e, isPinned)}>Send All to Registration</RegisterButton>
        //                 </ButtonToolbar>
        //             </Loader>
        //         </div>;
        //
        //     let entryCodesNeeded = RegisterButtonStore.getMissingEntryCodes();
        //     let entryCodesMessage;
        //     if (entryCodesNeeded.length > 0) {
        //         let entryCodesNeededEnd = entryCodesNeeded.pop();
        //         entryCodesMessage = <p className="mt-3 mb-0">{entryCodesNeeded.length === 0 ? entryCodesNeededEnd : entryCodesNeeded.join(", ") + " and " + entryCodesNeededEnd} require{entryCodesNeeded.length === 0 ? 's':''} an add/faculty code to send to Registration.</p>;
        //     }
        //
        //     footerHeading =
        //         <div className="mb-3 mx-0">
        //             {entryCodesMessage}
        //             {registrationIsOpenMessage}
        //         </div>;
        //
        // } else {
            footerHeading =
                <div className="mb-3 mx-0">
                    {/*<p className="mt-3 mb-0">*/}
                    {/*    <strong>Review & send this schedule to Registration.</strong> Any courses in your plan that are not included in this schedule*/}
                    {/*    option will be moved to your Backup. If there are any registration errors, those sections will be in your plan for*/}
                    {/*    you to try again.*/}
                    {/*</p>*/}
                    <p className="mt-3 mb-0">
                        <strong>Update My Plan</strong> to prepare to register for these sections.<br />
                        <small>Note: any courses in your plan that are not included in this schedule option will be moved to your Backup.</small>
                    </p>
                    {pinLegend}
                    {/*{registrationIsOpenMessage}*/}
                </div>;
        // }

        let modalBody =
            <Modal.Body>
                <RegistrationDateInfo termId={this.props.schedule.termId} />
                <ListGroup as="ul">
                    {courseList}
                </ListGroup>
                {enrollRestrictionLegend}
                {addCodeLegend}
                {timeMaskLegend}
                {dupEnrollLegend}
            </Modal.Body>;

        let feedbackMessage;
        if ( this.store.data.submitted ) {
            footerHeading = null;

            // if (this.store.showSendToRegistration) {
            //     modalTitle = "Your selected course sections have been sent to Registration";
            //     modalBody =
            //         <Modal.Body>
            //             <p>Your sections have been sent to Registration in a new tab.</p>
            //             <p>If your registration was successful, you'll see your updated schedule when you close this window.</p>
            //             <p><strong>You must carefully check your schedule in the Registration tab. If one or more of your sections has a registration error, you will not be registered for any sections.</strong></p>
            //             <p>If your registration had any errors, try one of the following steps:</p>
            //             <ol>
            //                 <li>Return to the Registration tab to see which errors need to be resolved.</li>
            //                 <li>Go back to the Schedule builder. Deselect the sections that weren't accepted in Planned Courses. Then click the "Update Schedule Options" button to generate new schedule options.</li>
            //                 <li>Try sending your chosen schedule to Registration again from Schedule Builder.</li>
            //                 <li>Note that any courses in your plan that are not sent to Registration will be moved to your Backup.</li>
            //             </ol>
            //         </Modal.Body>;
            // } else {
                modalBody =
                    <Modal.Body>
                        <div className="d-flex align-items-start text-success mb-3">
                            <FaCheck className="me-3 fs-5" aria-hidden={true} />
                            <div>
                                <p className="mb-0"><strong>{this.props.title}</strong> has been added to your plan.</p>
                                <p className="mb-0">
                                    <a href={termUrl}>
                                        View {TermUtils.makeTermDescriptionFromTermId(this.props.schedule.termId)} plan
                                    </a>
                                </p>
                            </div>
                        </div>
                    </Modal.Body>;
            // }

            modalFooter =
                <div className="mt-3 mx-0">
                    <ButtonToolbar className="d-flex justify-content-end">
                        <Button variant="primary" className="ms-auto px-4" onClick={() => this.cleanupAndHide()}>Close and Refresh</Button>
                    </ButtonToolbar>
                </div>;

        } else if (this.store.data.errMsg) {
            feedbackMessage =
                <Alert variant="danger" className="mb-3 mx-0">
                    We couldn't add {this.props.title} to your plan. {this.store.data.errMsg}
                </Alert>;
        }

        return (
            <Modal show={this.props.show}
                   onHide={() => this.cleanupAndHide()}
                   aria-labelledby="schedule-option-details-modal"
                   centered
                   scrollable>
                <Modal.Header closeButton>
                    <Modal.Title id="schedule-option-details-modal">{modalTitle}</Modal.Title>
                </Modal.Header>
                {possibleErrorsFeedback}
                {modalBody}
                <Modal.Footer className="d-block pt-0">
                    {footerHeading}
                    {feedbackMessage}
                    {modalFooter}
                </Modal.Footer>
            </Modal>
        );
    }
}

ScheduleBuilderOptionModal.propTypes = {
    schedule: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    isPinned: PropTypes.bool,
    isFromPinnedMenu: PropTypes.bool,
    regDate: PropTypes.string.isRequired,
    planData: PropTypes.array.isRequired
}

ScheduleBuilderOptionModal.defaultProps = {
    show: false,
    isPinned: false,
    isFromPinnedMenu: false
}

export default observer(ScheduleBuilderOptionModal);